// useAudioCueSync — Plan B step 5.
//
// Embed mode: advances scenes based on the moment audio playback began.
// The host page (voice_agent.html) dispatches:
//   - concierge:audio_started { detail: { at: performance.now() } }
//     on the FIRST PCM delta after a storyboard arrives.
//
// Implementation: instead of a rAF poll loop (which is throttled to 0Hz in
// headless Chromium and in background tabs), we use scheduled setTimeouts.
// One timeout per scene boundary, computed from anchor + target_start_time.
// Cancelled and rescheduled on storyboard change, anchor change, or
// interrupt. setTimeout fires on the macrotask queue regardless of paint.
//
// Fallback: if audio_started doesn't fire within FALLBACK_DELAY_MS after a
// storyboard arrives, anchor on the storyboard ingest timestamp + delay so
// scenes still advance (degraded but visible).

import { useEffect } from 'react';
import { usePresentationStore } from '../store/presentationStore';

const FALLBACK_DELAY_MS = 800;

export function useAudioCueSync(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    let scheduled: ReturnType<typeof setTimeout>[] = [];
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    const clearAll = () => {
      scheduled.forEach((t) => clearTimeout(t));
      scheduled = [];
      if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
    };

    const reschedule = (anchor: number) => {
      clearAll();
      const s = usePresentationStore.getState();
      if (!s.storyboard || !s.isPlaying) return;
      const scenes = s.storyboard.scenes;
      const now = performance.now();
      scenes.forEach((scene, i) => {
        if (i === 0) return; // scene 0 already showing
        const start = scene.target_start_time ?? 0;
        const fireAt = anchor + start;
        const delay = Math.max(0, fireAt - now);
        scheduled.push(
          setTimeout(() => {
            const live = usePresentationStore.getState();
            if (live.storyboard && live.isPlaying && live.currentSceneIndex < i) {
              live.setIndex(i);
            }
          }, delay),
        );
      });
      // Mark playback finished after the last scene's end-time elapses.
      const last = scenes[scenes.length - 1];
      if (last) {
        const fireAt = anchor + (last.target_end_time ?? 0);
        const delay = Math.max(0, fireAt - now);
        scheduled.push(
          setTimeout(() => {
            const live = usePresentationStore.getState();
            if (live.isPlaying) usePresentationStore.setState({ isPlaying: false });
          }, delay),
        );
      }
    };

    const onAudioStarted = (e: Event) => {
      const detail = (e as CustomEvent<{ at?: number }>).detail;
      const at = typeof detail?.at === 'number' ? detail.at : performance.now();
      usePresentationStore.getState().setPlaybackStart(at);
      reschedule(at);
      console.log('[v3-cue] audio anchored at', at.toFixed(1));
    };
    window.addEventListener('concierge:audio_started', onAudioStarted);

    // Watch the store for storyboard / interrupt transitions.
    const unsubscribe = usePresentationStore.subscribe((state, prev) => {
      // Storyboard changed (new ingest or interrupt clear).
      if (state.storyboard !== prev.storyboard) {
        clearAll();
        if (state.storyboard && state.storyboardIngestedAt !== null) {
          // Wait FALLBACK_DELAY_MS for audio_started before falling back to
          // wall-clock anchor on the ingest timestamp.
          fallbackTimer = setTimeout(() => {
            const live = usePresentationStore.getState();
            if (live.playbackStartedAt === null && live.storyboard) {
              const anchor = (live.storyboardIngestedAt ?? performance.now());
              live.setPlaybackStart(anchor);
              reschedule(anchor);
            }
          }, FALLBACK_DELAY_MS);
        }
      }
    });

    // Debug helper.
    (window as unknown as { __v3Store?: typeof usePresentationStore }).__v3Store = usePresentationStore;

    return () => {
      window.removeEventListener('concierge:audio_started', onAudioStarted);
      unsubscribe();
      clearAll();
    };
  }, [enabled]);
}
